/**
 * ThemeBuilderPro.tsx - Professional theme builder with Phase 1-3 features
 * 
 * CHANGE HISTORY:
 * - 251122_2340 danielbeer: Added Phase 1-3 enhancements
 *   Phase 1: Create new, duplicate, reset to defaults
 *   Phase 2: Search/filter, export/import, color palettes
 *   Phase 3: Live preview, undo/redo, CSS validation
 * 
 * Features:
 * - Create new skins from scratch
 * - Duplicate existing skins
 * - Export/Import skins as JSON
 * - Reset to defaults (per section or all)
 * - Variable search and filter
 * - Color palette presets (Material, Tailwind, GitHub, Dracula)
 * - Live preview panel
 * - Undo/Redo with history tracking
 * - CSS variable validation with error messages
 */

import React, { useState, useEffect } from 'react';
import type { Theme } from '../features/ThemeInheritance';
import { useBacktrackr } from '@opcentral/dependr';
import { auditCSSContent, generateAuditReport, type CSSAuditReport } from '../features/CSSAuditor';

export interface ThemeBuilderProProps {
  themeId?: string;
  skinnrContext?: any;
  onSave?: (theme: Theme) => void;
  onCancel?: () => void;
  onClose?: () => void;
}

interface HistoryEntry {
  variables: Record<string, string | number>;
  timestamp: number;
}

interface ColorPalette {
  name: string;
  colors: Record<string, string>;
}

const COLOR_PALETTES: ColorPalette[] = [
  {
    name: 'Material Blue',
    colors: {
      'color-primary': '#2196F3',
      'color-primary-hover': '#1976D2',
      'color-secondary': '#03A9F4',
      'color-success': '#4CAF50',
      'color-error': '#F44336',
      'color-warning': '#FF9800'
    }
  },
  {
    name: 'Tailwind',
    colors: {
      'color-primary': '#3B82F6',
      'color-primary-hover': '#2563EB',
      'color-secondary': '#8B5CF6',
      'color-success': '#10B981',
      'color-error': '#EF4444',
      'color-warning': '#F59E0B'
    }
  },
  {
    name: 'GitHub',
    colors: {
      'color-primary': '#0969DA',
      'color-primary-hover': '#0550AE',
      'color-secondary': '#6E7781',
      'color-success': '#1A7F37',
      'color-error': '#CF222E',
      'color-warning': '#9A6700'
    }
  },
  {
    name: 'Dracula',
    colors: {
      'color-primary': '#BD93F9',
      'color-primary-hover': '#9580FF',
      'color-secondary': '#FF79C6',
      'color-success': '#50FA7B',
      'color-error': '#FF5555',
      'color-warning': '#F1FA8C'
    }
  }
];

type SectionTab = 'colors' | 'typography' | 'spacing' | 'layout' | 'borders' | 'shadows' | 'templates';

interface Skin {
  id: string;
  name: string;
  description?: string;
  variables: Record<string, string | number>;
}

export const ThemeBuilderPro: React.FC<ThemeBuilderProProps> = ({
  themeId,
  skinnrContext,
  onSave,
  onCancel,
  onClose
}) => {
  const [activeSection, setActiveSection] = useState<SectionTab>('colors');
  const [selectedSkinId, setSelectedSkinId] = useState<string>(themeId || 'compact');
  const [availableSkins, setAvailableSkins] = useState<Skin[]>([]);
  const [currentSkin, setCurrentSkin] = useState<Skin | null>(null);
  const [originalVariables, setOriginalVariables] = useState<Record<string, string | number>>({});
  
  // Settings state
  const [variables, setVariables] = useState<Record<string, string | number>>({});
  const [hasChanges, setHasChanges] = useState(false);
  
  // Phase 1-3 state
  const [searchQuery, setSearchQuery] = useState('');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showPaletteDialog, setShowPaletteDialog] = useState(false);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditReport, setAuditReport] = useState<CSSAuditReport | null>(null);
  const [isAuditing, setIsAuditing] = useState(false);
  const [newSkinName, setNewSkinName] = useState('');
  const [newSkinId, setNewSkinId] = useState('');
  const [importData, setImportData] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  // Backtrackr integration for theme change tracking
  const backtrackr = useBacktrackr();

  useEffect(() => {
    // Load available skins from Skinnr context or use defaults
    let skins: Skin[] = [];
    
    if (skinnrContext && skinnrContext.getAllSkins) {
      skins = skinnrContext.getAllSkins();
    } else {
      // Use default skins from Skinnr package
      skins = [
        { id: 'compact', name: 'Compact (FileMaker Style)', description: 'Dense layout with minimal spacing', variables: {} },
        { id: 'grid-light', name: 'Grid Light Theme', description: 'Light theme for grid views', variables: {} },
        { id: 'grid-dark', name: 'Grid Dark Theme', description: 'Dark theme for grid views', variables: {} },
        { id: 'opcentral-dash', name: 'OpCentral Dashboard', description: 'Modern theme for OpCentral Dashboard', variables: {} }
      ];
    }
    
    setAvailableSkins(skins);

    // Load selected skin
    if (selectedSkinId) {
      let skin = skins.find((s: any) => s.id === selectedSkinId);
      
      // If skin not found in context, try to get it directly
      if (!skin && skinnrContext && skinnrContext.getSkin) {
        skin = skinnrContext.getSkin(selectedSkinId);
      }
      
      if (skin) {
        setCurrentSkin(skin);
        setVariables({ ...skin.variables });
        setOriginalVariables({ ...skin.variables });
        setHistory([{ variables: { ...skin.variables }, timestamp: Date.now() }]);
        setHistoryIndex(0);
        setHasChanges(false);
        setValidationErrors({});
      }
    }
  }, [selectedSkinId, skinnrContext]);

  const handleSkinChange = (skinId: string) => {
    if (hasChanges && !confirm('You have unsaved changes. Switch skins?')) {
      return;
    }
    setSelectedSkinId(skinId);
    setHasChanges(false);
  };

  const handleVariableChange = (key: string, value: string | number) => {
    const newVariables = { ...variables, [key]: value };
    setVariables(newVariables);
    setHasChanges(true);
    
    // Validate
    const error = validateVariable(key, value);
    if (error) {
      setValidationErrors(prev => ({ ...prev, [key]: error }));
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
    
    // Add to history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ variables: newVariables, timestamp: Date.now() });
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    
    // Track change with Backtrackr
    if (backtrackr && backtrackr.recordChange) {
      backtrackr.recordChange({
        type: 'theme_variable',
        context: `skin:${selectedSkinId}`,
        description: `Changed ${key} from "${variables[key]}" to "${value}"`,
        before: { [key]: variables[key] },
        after: { [key]: value },
        metadata: {
          variable: key,
          skinId: selectedSkinId,
          timestamp: Date.now()
        }
      });
    }
  };

  const validateVariable = (key: string, value: string | number): string | null => {
    const strValue = String(value);
    
    if (key.includes('color') || key.includes('bg-')) {
      if (!/^#[0-9A-Fa-f]{3,8}$/.test(strValue) && 
          !/^rgb/.test(strValue) && 
          !/^hsl/.test(strValue) &&
          !/^var\(/.test(strValue)) {
        return 'Invalid color format';
      }
    }
    
    if (key.includes('size') || key.includes('spacing') || key.includes('padding') || 
        key.includes('height') || key.includes('width') || key.includes('radius')) {
      if (!/^\d+(\.\d+)?(px|rem|em|%|vh|vw)$/.test(strValue) && strValue !== '0') {
        return 'Invalid size format (e.g., 10px, 1rem)';
      }
    }
    
    return null;
  };

  /**
   * Get variable source information
   * Returns where the variable is defined: skin, local override, inherited, or default
   */
  const getVariableSource = (key: string): {
    source: 'skin' | 'local' | 'inherited' | 'default';
    icon: string;
    label: string;
    color: string;
  } => {
    // Check if variable is in the original skin definition
    if (originalVariables[key] !== undefined) {
      return {
        source: 'skin',
        icon: '🎨',
        label: 'From Skin',
        color: 'var(--color-primary, #3b82f6)'
      };
    }
    
    // Check if it's a computed style from the DOM
    if (typeof window !== 'undefined') {
      const computedValue = getComputedStyle(document.documentElement).getPropertyValue(`--${key}`);
      if (computedValue && computedValue.trim()) {
        // Check if it's using var() - indicates inheritance
        if (computedValue.includes('var(')) {
          return {
            source: 'inherited',
            icon: '🔗',
            label: 'Inherited',
            color: 'var(--color-info, #3b82f6)'
          };
        }
        // Otherwise it's a local override
        return {
          source: 'local',
          icon: '📄',
          label: 'Local Override',
          color: 'var(--color-warning, #f59e0b)'
          };
      }
    }
    
    // Default fallback
    return {
      source: 'default',
      icon: '⚙️',
      label: 'Default',
      color: 'var(--text-tertiary, #9ca3af)'
    };
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setVariables({ ...history[newIndex].variables });
      setHasChanges(true);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setVariables({ ...history[newIndex].variables });
      setHasChanges(true);
    }
  };

  const handleCreateNew = () => {
    if (!newSkinName || !newSkinId) {
      alert('Please enter both skin name and ID');
      return;
    }
    
    const newSkin: Skin = {
      id: newSkinId,
      name: newSkinName,
      description: 'Custom skin',
      variables: {}
    };
    
    setAvailableSkins([...availableSkins, newSkin]);
    setSelectedSkinId(newSkinId);
    setShowCreateDialog(false);
    setNewSkinName('');
    setNewSkinId('');
  };

  const handleDuplicate = () => {
    if (!currentSkin) return;
    
    const newId = `${currentSkin.id}-copy`;
    const newName = `${currentSkin.name} (Copy)`;
    
    const duplicatedSkin: Skin = {
      id: newId,
      name: newName,
      description: currentSkin.description,
      variables: { ...variables }
    };
    
    setAvailableSkins([...availableSkins, duplicatedSkin]);
    setSelectedSkinId(newId);
  };

  const matchesSection = (key: string, section: SectionTab): boolean => {
    switch (section) {
      case 'colors': return key.includes('color') || key.includes('bg') || key.includes('text');
      case 'typography': return key.includes('font') || key.includes('line-height');
      case 'spacing': return key.includes('spacing') || key.includes('padding') || key.includes('gap');
      case 'layout': return key.includes('row-height') || key.includes('label-size') || key.includes('field-size') || key.includes('header-height');
      case 'borders': return key.includes('border');
      case 'shadows': return key.includes('shadow');
      default: return false;
    }
  };

  const handleResetSection = () => {
    if (!confirm(`Reset all ${activeSection} variables to defaults?`)) return;
    
    const resetVars = { ...variables };
    Object.keys(variables).forEach(key => {
      if (matchesSection(key, activeSection) && originalVariables[key] !== undefined) {
        resetVars[key] = originalVariables[key];
      }
    });
    
    setVariables(resetVars);
    setHasChanges(true);
  };

  const handleResetAll = () => {
    if (!confirm('Reset ALL variables to defaults? This cannot be undone.')) return;
    setVariables({ ...originalVariables });
    setHasChanges(true);
  };

  const handleExport = () => {
    if (!currentSkin) return;
    
    const exportData = {
      id: currentSkin.id,
      name: currentSkin.name,
      description: currentSkin.description,
      variables,
      exportedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentSkin.id}.skin.json`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportDialog(false);
  };

  const handleImport = () => {
    try {
      const imported = JSON.parse(importData);
      
      if (!imported.id || !imported.name || !imported.variables) {
        alert('Invalid skin file format');
        return;
      }
      
      const importedSkin: Skin = {
        id: imported.id,
        name: imported.name,
        description: imported.description,
        variables: imported.variables
      };
      
      setAvailableSkins([...availableSkins, importedSkin]);
      setSelectedSkinId(imported.id);
      setShowImportDialog(false);
      setImportData('');
    } catch (error) {
      alert('Failed to parse skin file. Please check the JSON format.');
    }
  };

  const handleApplyPalette = (palette: ColorPalette) => {
    const newVariables = { ...variables };
    Object.entries(palette.colors).forEach(([key, value]) => {
      if (newVariables[key] !== undefined) {
        newVariables[key] = value;
      }
    });
    setVariables(newVariables);
    setHasChanges(true);
    setShowPaletteDialog(false);
  };

  const handleRunAudit = async () => {
    setIsAuditing(true);
    setAuditReport(null);
    
    try {
      // In a browser environment, we can only audit inline styles
      // For full package scanning, use the CLI tool
      const fileContents = new Map<string, string>();
      
      // Scan all style tags in the document
      const styleTags = document.querySelectorAll('style');
      styleTags.forEach((tag, index) => {
        if (tag.textContent) {
          fileContents.set(`inline-style-${index}.css`, tag.textContent);
        }
      });
      
      // Scan all link tags for CSS
      const linkTags = document.querySelectorAll('link[rel="stylesheet"]');
      linkTags.forEach((link, index) => {
        const href = link.getAttribute('href');
        if (href) {
          // Note: Can't actually fetch cross-origin CSS in browser
          fileContents.set(href, '/* External CSS - use CLI tool for full audit */');
        }
      });
      
      // Generate report
      const report = generateAuditReport(fileContents);
      setAuditReport(report);
    } catch (error) {
      console.error('Audit failed:', error);
      alert('Audit failed. See console for details.');
    } finally {
      setIsAuditing(false);
    }
  };

  const filterVariables = (vars: [string, string | number][]): [string, string | number][] => {
    if (!searchQuery) return vars;
    return vars.filter(([key]) => 
      key.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const handleSave = () => {
    if (!currentSkin) return;
    
    if (Object.keys(validationErrors).length > 0) {
      alert('Please fix validation errors before saving');
      return;
    }

    const theme: Theme = {
      id: currentSkin.id,
      name: currentSkin.name,
      variables
    };

    if (onSave) {
      onSave(theme);
    }
    setHasChanges(false);
    setOriginalVariables({ ...variables });
  };

  const handleCancel = () => {
    if (hasChanges && !confirm('You have unsaved changes. Are you sure?')) {
      return;
    }
    if (onCancel) {
      onCancel();
    } else if (onClose) {
      onClose();
    }
  };

  const handlePreview = () => {
    // Apply variables to document root for live preview
    const root = document.documentElement;
    Object.entries(variables).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, String(value));
    });
  };

  const renderColorSection = () => {
    const colorVars = Object.entries(variables).filter(([key]) => 
      key.includes('color') || key.includes('bg') || key.includes('text')
    );
    const filtered = filterVariables(colorVars);

    return (
      <div className="section-content">
        <div className="section-header-row">
          <h3>Colors</h3>
          <button onClick={handleResetSection} className="btn-text">
            ↺ Reset Section
          </button>
        </div>
        <div className="source-legend">
          <div className="source-legend-item">
            <span className="source-legend-icon">🎨</span>
            <span>From Skin</span>
          </div>
          <div className="source-legend-item">
            <span className="source-legend-icon">📄</span>
            <span>Local Override</span>
          </div>
          <div className="source-legend-item">
            <span className="source-legend-icon">🔗</span>
            <span>Inherited</span>
          </div>
          <div className="source-legend-item">
            <span className="source-legend-icon">⚙️</span>
            <span>Default</span>
          </div>
        </div>
        {filtered.length === 0 ? (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '24px' }}>
            {searchQuery ? 'No variables match your search' : 'No color variables'}
          </p>
        ) : (
          <div className="settings-grid">
            {filtered.map(([key, value]) => {
              const source = getVariableSource(key);
              return (
                <div key={key} className="setting-row">
                  <label>
                    {key.replace(/-/g, ' ').replace(/^color /, '')}
                    <span 
                      className="variable-source-badge" 
                      title={source.label}
                      style={{ color: source.color }}
                    >
                      {source.icon}
                    </span>
                  </label>
                  <div className="input-group">
                    <div className="color-input-group">
                      <input
                        type="color"
                        value={String(value).startsWith('#') ? String(value) : '#000000'}
                        onChange={(e) => handleVariableChange(key, e.target.value)}
                      />
                      <input
                        type="text"
                        value={String(value)}
                        onChange={(e) => handleVariableChange(key, e.target.value)}
                        className={validationErrors[key] ? 'error' : ''}
                        style={{ fontFamily: 'monospace', fontSize: '12px' }}
                      />
                    </div>
                    {validationErrors[key] && (
                      <span className="error-message">{validationErrors[key]}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderTypographySection = () => {
    const typoVars = Object.entries(variables).filter(([key]) => 
      key.includes('font') || key.includes('line-height')
    );

    return (
      <div className="section-content">
        <h3>Typography</h3>
        <div className="settings-grid">
          {typoVars.map(([key, value]) => (
            <div key={key} className="setting-row">
              <label>{key.replace(/-/g, ' ')}</label>
              <input
                type="text"
                value={String(value)}
                onChange={(e) => handleVariableChange(key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSpacingSection = () => {
    const spacingVars = Object.entries(variables).filter(([key]) => 
      key.includes('spacing') || key.includes('padding') || key.includes('gap')
    );

    return (
      <div className="section-content">
        <h3>Spacing & Padding</h3>
        <div className="settings-grid">
          {spacingVars.map(([key, value]) => (
            <div key={key} className="setting-row">
              <label>{key.replace(/-/g, ' ')}</label>
              <input
                type="text"
                value={String(value)}
                onChange={(e) => handleVariableChange(key, e.target.value)}
                placeholder="e.g., 4px, 0.5rem"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLayoutSection = () => {
    const layoutVars = Object.entries(variables).filter(([key]) => 
      key.includes('row-height') || key.includes('label-size') || 
      key.includes('field-size') || key.includes('header-height')
    );

    return (
      <div className="section-content">
        <h3>Layout Dimensions</h3>
        <div className="settings-grid">
          {layoutVars.map(([key, value]) => (
            <div key={key} className="setting-row">
              <label>{key.replace(/-/g, ' ')}</label>
              <input
                type="text"
                value={String(value)}
                onChange={(e) => handleVariableChange(key, e.target.value)}
                placeholder="e.g., 20px, 1.5rem"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderBordersSection = () => {
    const borderVars = Object.entries(variables).filter(([key]) => 
      key.includes('border-radius') || key.includes('border')
    );

    return (
      <div className="section-content">
        <h3>Borders & Radius</h3>
        <div className="settings-grid">
          {borderVars.map(([key, value]) => (
            <div key={key} className="setting-row">
              <label>{key.replace(/-/g, ' ')}</label>
              <input
                type="text"
                value={String(value)}
                onChange={(e) => handleVariableChange(key, e.target.value)}
                placeholder="e.g., 4px, 0.25rem"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderShadowsSection = () => {
    const shadowVars = Object.entries(variables).filter(([key]) => 
      key.includes('shadow')
    );

    return (
      <div className="section-content">
        <h3>Shadows</h3>
        <div className="settings-grid">
          {shadowVars.map(([key, value]) => (
            <div key={key} className="setting-row">
              <label>{key.replace(/-/g, ' ')}</label>
              <input
                type="text"
                value={String(value)}
                onChange={(e) => handleVariableChange(key, e.target.value)}
                placeholder="e.g., 0 2px 4px rgba(0,0,0,0.1)"
                style={{ fontFamily: 'monospace', fontSize: '11px' }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTemplatesSection = () => {
    return (
      <div className="section-content">
        <h3>Templatr Templates</h3>
        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
          Load pre-configured theme templates from Templatr
        </p>
        <div className="template-list">
          <button className="template-button">
            📄 FileMaker Classic
            <span>Traditional FileMaker layout styling</span>
          </button>
          <button className="template-button">
            🎨 Modern Minimal
            <span>Clean, modern interface with generous spacing</span>
          </button>
          <button className="template-button">
            🌙 Dark Professional
            <span>Professional dark theme for extended use</span>
          </button>
          <button className="template-button">
            💼 Enterprise
            <span>Corporate-friendly color scheme</span>
          </button>
        </div>
      </div>
    );
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'colors': return renderColorSection();
      case 'typography': return renderTypographySection();
      case 'spacing': return renderSpacingSection();
      case 'layout': return renderLayoutSection();
      case 'borders': return renderBordersSection();
      case 'shadows': return renderShadowsSection();
      case 'templates': return renderTemplatesSection();
      default: return null;
    }
  };

  return (
    <div className="theme-builder-pro">
      {/* Header */}
      <div className="theme-builder-header">
        <div className="header-left">
          <h2>🎨 Theme Builder Pro</h2>
          <select 
            value={selectedSkinId} 
            onChange={(e) => handleSkinChange(e.target.value)}
            className="skin-selector"
          >
            <option value="">Select a skin...</option>
            {availableSkins.map(skin => (
              <option key={skin.id} value={skin.id}>
                {skin.name}
              </option>
            ))}
          </select>
          <button onClick={() => setShowCreateDialog(true)} className="btn-icon" title="Create new skin">
            ➕
          </button>
          <button onClick={handleDuplicate} className="btn-icon" title="Duplicate" disabled={!currentSkin}>
            📋
          </button>
        </div>
        <div className="header-right">
          <button onClick={handleUndo} className="btn-icon" title="Undo" disabled={historyIndex <= 0}>
            ↶
          </button>
          <button onClick={handleRedo} className="btn-icon" title="Redo" disabled={historyIndex >= history.length - 1}>
            ↷
          </button>
          <button onClick={handleCancel} className="btn-secondary">
            ✕ Close
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-left">
          <input
            type="text"
            placeholder="🔍 Search variables..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {activeSection === 'colors' && (
            <button onClick={() => setShowPaletteDialog(true)} className="btn-text">
              🎨 Apply Palette
            </button>
          )}
        </div>
        <div className="toolbar-right">
          <button onClick={() => setShowAuditDialog(true)} className="btn-text" title="Audit CSS for hardcoded values">
            🔍 Audit CSS
          </button>
          <button onClick={() => setShowExportDialog(true)} className="btn-text" disabled={!currentSkin}>
            📤 Export
          </button>
          <button onClick={() => setShowImportDialog(true)} className="btn-text">
            📥 Import
          </button>
          <button onClick={handleResetAll} className="btn-text">
            ↺ Reset All
          </button>
          {backtrackr && backtrackr.canUndo && (
            <button onClick={() => backtrackr.undo()} className="btn-text" title="Revert to previous version">
              ⏮️ Revert
            </button>
          )}
        </div>
      </div>

      {/* Section Tabs */}
      <div className="section-tabs">
        <button 
          className={activeSection === 'colors' ? 'active' : ''}
          onClick={() => setActiveSection('colors')}
        >
          🎨 Colors
        </button>
        <button 
          className={activeSection === 'typography' ? 'active' : ''}
          onClick={() => setActiveSection('typography')}
        >
          📝 Typography
        </button>
        <button 
          className={activeSection === 'spacing' ? 'active' : ''}
          onClick={() => setActiveSection('spacing')}
        >
          📏 Spacing
        </button>
        <button 
          className={activeSection === 'layout' ? 'active' : ''}
          onClick={() => setActiveSection('layout')}
        >
          📐 Layout
        </button>
        <button 
          className={activeSection === 'borders' ? 'active' : ''}
          onClick={() => setActiveSection('borders')}
        >
          ⬜ Borders
        </button>
        <button 
          className={activeSection === 'shadows' ? 'active' : ''}
          onClick={() => setActiveSection('shadows')}
        >
          🌑 Shadows
        </button>
        <button 
          className={activeSection === 'templates' ? 'active' : ''}
          onClick={() => setActiveSection('templates')}
        >
          📄 Templates
        </button>
      </div>

      {/* Content */}
      <div className="theme-builder-content">
        {currentSkin ? (
          <>
            <div className="skin-info">
              <h4>{currentSkin.name}</h4>
              {currentSkin.description && <p>{currentSkin.description}</p>}
              {Object.keys(validationErrors).length > 0 && (
                <div className="validation-warning">
                  ⚠️ {Object.keys(validationErrors).length} validation error(s)
                </div>
              )}
            </div>
            {renderSection()}
          </>
        ) : (
          <div className="no-skin-selected">
            <p>Select a skin from the dropdown to begin editing</p>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="theme-builder-footer">
        <div className="footer-left">
          {hasChanges && (
            <span className="changes-indicator">● Unsaved changes</span>
          )}
          {historyIndex > 0 && (
            <span className="history-indicator">
              {historyIndex} / {history.length - 1} changes
            </span>
          )}
        </div>
        <div className="footer-right">
          <button onClick={handlePreview} className="btn-secondary">
            👁️ Preview
          </button>
          <button onClick={handleCancel} className="btn-secondary">
            Cancel
          </button>
          <button 
            onClick={handleSave} 
            className="btn-primary"
            disabled={!hasChanges || Object.keys(validationErrors).length > 0}
          >
            💾 Save Theme
          </button>
        </div>
      </div>

      <style>{`
        .theme-builder-pro {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: var(--surface-base, #ffffff);
          font-family: system-ui, -apple-system, sans-serif;
        }

        .section-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .input-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .error-message {
          color: var(--error-text, #ef4444);
          font-size: 11px;
        }

        input.error {
          border-color: var(--error-border, #ef4444);
        }

        .theme-builder-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          border-bottom: 1px solid var(--border-default, #e5e7eb);
          background: var(--surface-raised, #f9fafb);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .header-left h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
        }

        .skin-selector {
          padding: 8px 12px;
          border: 1px solid var(--border-default, #e5e7eb);
          border-radius: 6px;
          font-size: 14px;
          min-width: 250px;
        }

        .section-tabs {
          display: flex;
          gap: 4px;
          padding: 12px 24px;
          border-bottom: 1px solid var(--border-default, #e5e7eb);
          background: var(--surface-raised, #f9fafb);
          overflow-x: auto;
        }

        .section-tabs button {
          padding: 8px 16px;
          border: none;
          background: transparent;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.2s;
        }

        .section-tabs button:hover {
          background: var(--interactive-hover, rgba(0,0,0,0.05));
        }

        .section-tabs button.active {
          background: var(--primary-bg, #3b82f6);
          color: white;
        }

        .theme-builder-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
        }

        .skin-info {
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid var(--border-default, #e5e7eb);
        }

        .skin-info h4 {
          margin: 0 0 8px 0;
          font-size: 18px;
        }

        .skin-info p {
          margin: 0;
          color: var(--text-secondary, #6b7280);
          font-size: 14px;
        }

        .section-content h3 {
          margin: 0 0 16px 0;
          font-size: 16px;
          font-weight: 600;
        }

        .settings-grid {
          display: grid;
          gap: 16px;
        }

        .setting-row {
          display: grid;
          grid-template-columns: 200px 1fr;
          align-items: center;
          gap: 12px;
        }

        .setting-row label {
          font-size: 14px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .setting-row input[type="text"],
        .setting-row input[type="number"] {
          padding: 6px 10px;
          border: 1px solid var(--border-default, #e5e7eb);
          border-radius: 4px;
          font-size: 13px;
        }

        .color-input-group {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .color-input-group input[type="color"] {
          width: 50px;
          height: 36px;
          border: 1px solid var(--border-default, #e5e7eb);
          border-radius: 4px;
          cursor: pointer;
        }

        .color-input-group input[type="text"] {
          flex: 1;
        }

        .template-list {
          display: grid;
          gap: 12px;
        }

        .template-button {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 16px;
          border: 1px solid var(--border-default, #e5e7eb);
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 14px;
          font-weight: 500;
        }

        .template-button:hover {
          border-color: var(--primary-border, #3b82f6);
          background: var(--interactive-hover, rgba(0,0,0,0.05));
        }

        .template-button span {
          font-size: 12px;
          font-weight: 400;
          color: var(--text-secondary, #6b7280);
          margin-top: 4px;
        }

        .no-skin-selected {
          text-align: center;
          padding: 48px;
          color: var(--text-secondary, #6b7280);
        }

        .theme-builder-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          border-top: 1px solid var(--border-default, #e5e7eb);
          background: var(--surface-raised, #f9fafb);
        }

        .footer-right {
          display: flex;
          gap: 12px;
        }

        .changes-indicator {
          color: var(--warning-text, #f59e0b);
          font-size: 14px;
          font-weight: 500;
        }

        .btn-primary {
          padding: 10px 20px;
          background: var(--primary-bg, #3b82f6);
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary:hover:not(:disabled) {
          background: var(--color-primary-hover, #1d4ed8);
        }

        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-secondary {
          padding: 10px 20px;
          background: white;
          color: var(--text-primary, #1f2937);
          border: 1px solid var(--border-default, #e5e7eb);
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-secondary:hover {
          background: var(--interactive-hover, rgba(0,0,0,0.05));
          border-color: var(--primary-border, #3b82f6);
        }

        .btn-icon {
          padding: 6px 10px;
          background: transparent;
          border: 1px solid var(--border-default, #e5e7eb);
          border-radius: 4px;
          cursor: pointer;
          font-size: 16px;
        }

        .btn-icon:hover:not(:disabled) {
          background: var(--interactive-hover, rgba(0,0,0,0.05));
        }

        .btn-icon:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .btn-text {
          padding: 6px 12px;
          background: transparent;
          border: none;
          cursor: pointer;
          font-size: 13px;
          color: var(--text-primary);
        }

        .btn-text:hover:not(:disabled) {
          text-decoration: underline;
        }

        .btn-text:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 20px;
          border-bottom: 1px solid var(--border-default, #e5e7eb);
          background: var(--surface-raised, #f9fafb);
        }

        .toolbar-left, .toolbar-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .search-input {
          padding: 6px 12px;
          border: 1px solid var(--border-default, #e5e7eb);
          border-radius: 4px;
          min-width: 250px;
          font-size: 13px;
        }

        .validation-warning {
          color: var(--color-warning, #f59e0b);
          padding: 8px 12px;
          background: var(--warning-bg-subtle, #fef3c7);
          border-radius: 4px;
          margin-top: 8px;
          font-size: 13px;
        }

        .history-indicator {
          color: var(--text-secondary, #6b7280);
          font-size: 13px;
          margin-left: 12px;
        }

        .dialog-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10001;
        }

        .dialog {
          background: white;
          padding: 24px;
          border-radius: 8px;
          min-width: 400px;
          max-width: 600px;
          max-height: 80vh;
          overflow-y: auto;
        }

        .dialog h3 {
          margin: 0 0 16px 0;
        }

        .dialog input,
        .dialog textarea {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid var(--border-default, #e5e7eb);
          border-radius: 4px;
          margin-bottom: 12px;
          font-size: 13px;
        }

        .dialog textarea {
          font-family: monospace;
          font-size: 12px;
        }

        .dialog-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
          margin-top: 16px;
        }

        .palette-grid {
          display: grid;
          gap: 12px;
        }

        .palette-button {
          display: flex;
          flex-direction: column;
          padding: 12px;
          border: 1px solid var(--border-default, #e5e7eb);
          border-radius: 6px;
          background: white;
          cursor: pointer;
          text-align: left;
        }

        .palette-button:hover {
          border-color: var(--primary-border, #3b82f6);
          background: var(--interactive-hover, rgba(0,0,0,0.05));
        }

        .palette-name {
          font-weight: 600;
          margin-bottom: 8px;
        }

        .palette-colors {
          display: flex;
          gap: 4px;
        }

        .palette-swatch {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          border: 1px solid rgba(0,0,0,0.1);
        }

        .variable-source-badge {
          margin-left: 6px;
          font-size: 14px;
          cursor: help;
          opacity: 0.8;
          transition: opacity 0.2s;
        }

        .variable-source-badge:hover {
          opacity: 1;
        }

        .source-legend {
          display: flex;
          gap: 16px;
          padding: 12px;
          background: var(--surface-raised, #f9fafb);
          border-radius: 6px;
          margin-bottom: 16px;
          font-size: 12px;
        }

        .source-legend-item {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .source-legend-icon {
          font-size: 14px;
        }
      `}</style>

      {/* Create Dialog */}
      {showCreateDialog && (
        <div className="dialog-overlay" onClick={() => setShowCreateDialog(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Skin</h3>
            <label>
              Skin Name:
              <input
                type="text"
                value={newSkinName}
                onChange={(e) => setNewSkinName(e.target.value)}
                placeholder="My Custom Skin"
              />
            </label>
            <label>
              Skin ID:
              <input
                type="text"
                value={newSkinId}
                onChange={(e) => setNewSkinId(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                placeholder="my-custom-skin"
              />
            </label>
            <div className="dialog-actions">
              <button onClick={() => setShowCreateDialog(false)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleCreateNew} className="btn-primary">
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Dialog */}
      {showExportDialog && (
        <div className="dialog-overlay" onClick={() => setShowExportDialog(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Export Skin</h3>
            <p>Export "{currentSkin?.name}" as JSON file?</p>
            <div className="dialog-actions">
              <button onClick={() => setShowExportDialog(false)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleExport} className="btn-primary">
                📤 Export
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Dialog */}
      {showImportDialog && (
        <div className="dialog-overlay" onClick={() => setShowImportDialog(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Import Skin</h3>
            <label>
              Paste JSON:
              <textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder='{"id": "...", "name": "...", "variables": {...}}'
                rows={10}
              />
            </label>
            <div className="dialog-actions">
              <button onClick={() => setShowImportDialog(false)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleImport} className="btn-primary">
                📥 Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS Audit Dialog */}
      {showAuditDialog && (
        <div className="dialog-overlay" onClick={() => setShowAuditDialog(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()} style={{ minWidth: '700px', maxWidth: '900px', maxHeight: '90vh' }}>
            <h3>🔍 CSS Audit Report</h3>
            
            {!auditReport ? (
              <>
                <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
                  Scan CSS for hardcoded values that should use skin variables.
                  This helps maintain consistency and makes dark mode easier.
                </p>
                
                <div style={{ 
                  padding: '14px', 
                  background: '#fff3cd', 
                  border: '2px solid #f59e0b',
                  borderRadius: '6px',
                  fontSize: '13px',
                  marginBottom: '16px'
                }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#92400e' }}>
                    ⚠️ Browser Limitations
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#78350f' }}>
                    <li><strong>Only scans inline &lt;style&gt; tags</strong> in the current page</li>
                    <li><strong>Cannot access external CSS files</strong> (cross-origin restrictions)</li>
                    <li><strong>Cannot scan package source files</strong> (no file system access)</li>
                  </ul>
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #fbbf24' }}>
                    <strong>For complete package scanning:</strong><br/>
                    Run from terminal: <code style={{ background: '#fff', padding: '2px 8px', borderRadius: '3px', fontWeight: 'bold' }}>npm run audit:css</code>
                  </div>
                </div>
                
                <div style={{ 
                  padding: '16px', 
                  background: 'var(--surface-raised, #f9fafb)', 
                  borderRadius: '6px',
                  marginBottom: '16px'
                }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>What gets flagged:</h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#6b7280' }}>
                    <li>❌ <strong>Errors</strong>: Hardcoded text/background colors (readability issues)</li>
                    <li>⚠️ <strong>Warnings</strong>: Interactive state colors (hover, active, focus)</li>
                    <li>ℹ️ <strong>Info</strong>: Hardcoded spacing and sizing values</li>
                  </ul>
                </div>
                <div className="dialog-actions">
                  <button onClick={() => setShowAuditDialog(false)} className="btn-secondary">
                    Cancel
                  </button>
                  <button onClick={handleRunAudit} className="btn-primary" disabled={isAuditing}>
                    {isAuditing ? '⏳ Scanning...' : '🔍 Run Audit'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ 
                  padding: '16px', 
                  background: auditReport.totalIssues === 0 ? '#d1fae5' : '#fee2e2',
                  border: `1px solid ${auditReport.totalIssues === 0 ? '#10b981' : '#ef4444'}`,
                  borderRadius: '6px',
                  marginBottom: '16px'
                }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>
                    {auditReport.totalIssues === 0 ? '✅ No Issues Found!' : `⚠️ Found ${auditReport.totalIssues} Issues`}
                  </h4>
                  {auditReport.totalIssues > 0 && (
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      <div>❌ Errors: {auditReport.bySeverity.error || 0}</div>
                      <div>⚠️ Warnings: {auditReport.bySeverity.warning || 0}</div>
                      <div>ℹ️ Info: {auditReport.bySeverity.info || 0}</div>
                    </div>
                  )}
                </div>
                
                {auditReport.totalIssues > 0 && (
                  <>
                    <div style={{ 
                      padding: '10px 12px', 
                      background: '#fef3c7', 
                      border: '1px solid #f59e0b',
                      borderRadius: '4px',
                      fontSize: '12px',
                      marginBottom: '16px',
                      color: '#78350f'
                    }}>
                      ℹ️ <strong>Note:</strong> This only scanned inline styles. Run <code style={{ background: '#fff', padding: '1px 4px', borderRadius: '2px' }}>npm run audit:css</code> for complete package scanning.
                    </div>
                    <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '16px' }}>
                      <h4 style={{ fontSize: '14px', marginBottom: '12px' }}>Issues by Category:</h4>
                    {Object.entries(auditReport.byCategory).map(([category, count]) => (
                      <div key={category} style={{ 
                        padding: '8px 12px', 
                        background: 'var(--surface-raised, #f9fafb)',
                        borderRadius: '4px',
                        marginBottom: '8px',
                        fontSize: '13px'
                      }}>
                        <strong style={{ textTransform: 'capitalize' }}>{category}:</strong> {count} issues
                      </div>
                    ))}
                    
                    <h4 style={{ fontSize: '14px', margin: '16px 0 12px 0' }}>Top Issues (first 10):</h4>
                    {auditReport.issues.slice(0, 10).map((issue, idx) => (
                      <div key={idx} style={{ 
                        padding: '12px', 
                        background: 'var(--surface-raised, #f9fafb)',
                        borderLeft: `3px solid ${issue.severity === 'error' ? '#ef4444' : issue.severity === 'warning' ? '#f59e0b' : '#3b82f6'}`,
                        borderRadius: '4px',
                        marginBottom: '8px',
                        fontSize: '12px',
                        fontFamily: 'monospace'
                      }}>
                        <div style={{ marginBottom: '4px', color: '#6b7280' }}>{issue.file}:{issue.line}</div>
                        <div style={{ marginBottom: '4px' }}>{issue.property}: <strong>{issue.value}</strong></div>
                        <div style={{ color: '#10b981' }}>→ Suggest: <strong>{issue.suggestedVariable}</strong></div>
                      </div>
                    ))}
                    </div>
                  </>
                )}
                
                <div className="dialog-actions">
                  <button onClick={() => { setAuditReport(null); setShowAuditDialog(false); }} className="btn-secondary">
                    Close
                  </button>
                  <button onClick={handleRunAudit} className="btn-primary">
                    🔄 Run Again
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Palette Dialog */}
      {showPaletteDialog && (
        <div className="dialog-overlay" onClick={() => setShowPaletteDialog(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Apply Color Palette</h3>
            <div className="palette-grid">
              {COLOR_PALETTES.map(palette => (
                <button
                  key={palette.name}
                  className="palette-button"
                  onClick={() => handleApplyPalette(palette)}
                >
                  <div className="palette-name">{palette.name}</div>
                  <div className="palette-colors">
                    {Object.values(palette.colors).map((color, i) => (
                      <div key={i} className="palette-swatch" style={{ background: color }} />
                    ))}
                  </div>
                </button>
              ))}
            </div>
            <div className="dialog-actions">
              <button onClick={() => setShowPaletteDialog(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeBuilderPro;
